import {defineStore, storeToRefs} from "pinia";
import { ref } from 'vue';
import { useAuthStore } from "./authStore.js";
import { authRoot, deviceRoot } from "../vue-client.js";
import { usePreferenceStore } from "./preferenceStore.js";
import { getCompensatedDataset } from "../utilities/slotUtilities";
import { ExportUtility } from "../utilities/exportUtility";
import {useDurationStore} from "./durationStore.ts";

export const useSlotStore = defineStore('slot', () => {
  const slots = ref(null); // Start with uninitialized
  const currentSlot = ref(null); // Slot object
  const deviceHistory = ref([]); // Slot history objects
  const deviceHistoryN = ref([])
  const deviceSnapshot = ref(null); // Slot snapshot object
  const deviceTimeZone = ref(null); // Time zone object
  const accountUsers = ref([]); // All users (if any)
  const timeSeriesHistory = ref(null); // Time series results
  const deviceWeatherLabel = ref('');

  // Cache slots internally
  async function fetchAccountSlots() {
    if (slots.value === null) {
      const authStore = useAuthStore();
      const authString = "Bearer " + authStore.authUser.authToken;
      const fetchString = `${deviceRoot}/snapshots/`;
      const response = await fetch(fetchString, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: authString,
        },
      });

      const data = await response.json();
      slots.value = data;
    }
  }

    async function fetchSlotSummary(id) {
    if (slots.value !== null) {
      const authStore = useAuthStore();
      const authString = "Bearer " + authStore.authUser.authToken;
      const fetchString = `${deviceRoot}/snapshots/${id}`;
      const response = await fetch(fetchString, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: authString,
        },
      });

      const data = await response.json();
      // Update the corresponding slot
      const slotIndex = slots.value.findIndex(slot => slot._id === id);
      if (slotIndex !== -1) {
        slots.value[slotIndex] = data;
      }
    }
  }

  async function createSlot(payload) {
    const authStore = useAuthStore();
    const authString = "Bearer " + authStore.authUser.authToken;
    const fetchString = `${deviceRoot}/slots`;
    const response = await fetch(fetchString, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: authString,
      },
      body: JSON.stringify(payload),
    });
  }

  async function fetchSlot(payload) {
    const authStore = useAuthStore();
    const authString = "Bearer " + authStore.authUser.authToken;
    const slotId = payload.id;
    const fetchString = `${deviceRoot}/slots/${slotId}`;
    const response = await fetch(fetchString, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: authString,
      },
    });

    const data = await response.json();
    currentSlot.value = data;
  }

  async function updateSlot(slotId, payload) {
    const authStore = useAuthStore();
    const authString = "Bearer " + authStore.authUser.authToken;
    const fetchString = `${deviceRoot}/slots/${slotId}`;
    const response = await fetch(fetchString, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: authString,
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    currentSlot.value = data;
    return data;
  }

  async function fetchNdvi(slotId) {
    const authStore = useAuthStore();
    const authString = "Bearer " + authStore.authUser.authToken;
    const fetchString = `${deviceRoot}/earth/slots/${slotId}`;
    const response = await fetch(fetchString, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: authString,
      },
    });

    const data = await response.json();
    return data.mapId;
  }

  async function fetchDeviceHistory(payload) {
    const authStore = useAuthStore();
    const authString = "Bearer " + authStore.authUser.authToken;
    // let fetchString = `${deviceRoot}/devices/${payload.slotId}/history/v2`;
    let fetchString = `${deviceRoot}/slots/${payload.slotId}/configuration/history`;
    const mock = (import.meta.env.VITE_APP_Mock === 'true');
    if (mock) {
      fetchString += `?mock=true`;
    }
    
    const response = await fetch(fetchString, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: authString,
      },
      body: JSON.stringify(payload),
    });

    const hisReport = await response.json();
    hisReport.forEach((entry) => {
      entry.timestamp = new Date(entry.handshakeTime);
      entry.interpolated = false;
    });
    const startTime = useDurationStore().startDateTime + 48 * 3600 * 1000
    let filterHisReport = hisReport.filter((entry) => entry.timestamp.getTime() >= startTime)
    const sensorMap = ExportUtility.computeSensorValues(filterHisReport);
    const v0History = ExportUtility.getV0Histories(sensorMap, payload.slotId);
    (deviceHistory as any).value = v0History;

    const sensorMapN = ExportUtility.computeSensorValues(hisReport);
    const v0HistoryN = ExportUtility.getV0Histories(sensorMapN, payload.slotId);
    (deviceHistoryN as any).value = v0HistoryN;
  }

  async function fetchTimeSeries(slotId, payload) {
    const authStore = useAuthStore();
    const authString = "Bearer " + authStore.authUser.authToken;
    const fetchString = `${deviceRoot}/slots/${slotId}/timeseries`;
    const response = await fetch(fetchString, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: authString,
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    timeSeriesHistory.value = data;
  }

  // Return cloned version of device history
  function deepClone(obj) {
    if (Array.isArray(obj)) {
      return obj.map((item) => deepClone(item));
    } else if (typeof obj === 'object' && obj !== null) {
      const clone = {};
      for (let key in obj) {
        if (obj.hasOwnProperty(key)) {
          clone[key] = deepClone(obj[key]);
        }
      }
      return clone;
    } else {
      return obj;
    }
  }

  // Store for measurement corrections
  const measurementCorrections = ref([]);

  async function fetchMeasurementCorrections() {
    const authStore = useAuthStore();
    const authString = "Bearer " + authStore.authUser.authToken;
    const fetchString = `${deviceRoot}/measurement-corrections`;
    const response = await fetch(fetchString, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: authString,
      },
    });
    const data = await response.json();
    measurementCorrections.value = data;
  }

  function getCompensationParams(sensorName) {
    const correction = measurementCorrections.value.find(
      (item) => item.measurementName === sensorName && item.enabled
    );
    return correction ? correction.parameters : null;
  }

  function computeCompensatedHistoryGeneric(historyRef) {
    const clonedResult = deepClone(historyRef.value);
    const preferenceStore = usePreferenceStore();
    const sensorsSelected = preferenceStore.getSensorSelected();
    if (
      clonedResult &&
      clonedResult.sensors &&
      clonedResult.sensors.length > 0 &&
      clonedResult.sensors[0].history.length > 0
    ) {
      clonedResult.sensors.forEach((sensor) => {
        const sensorName = sensor.sensorName;
        if (sensorsSelected.some((item) => item.sensor === sensorName && item.selected)) {
          const params = getCompensationParams(sensorName);
          if (params && params.compensationSensorName) {
            const compSensorName = params.compensationSensorName;
            const compDataset = getCompensatedDataset(sensorsSelected, clonedResult, sensorName, 'y_axis_1', compSensorName);
            const compDatasetHistory = compDataset.data.reverse();
            for (let index = 0; index < sensor.history.length; index++) {
              sensor.history[index].val = compDatasetHistory[index];
            }
          }
        }
      });
    }
    return clonedResult;
  }

  function computeCompensatedHistory() {
    return computeCompensatedHistoryGeneric(deviceHistory);
  }

  function computeLargeCompensatedHistory() {
    return computeCompensatedHistoryGeneric(deviceHistoryN);
  }

  function spikeRemovalImpl(points, spikeThreshold) {
    if (points.length < 3) {
      return points; // Need at least 3 points for comparison
    }
    // 1. 计算平均值
    const mean = points.reduce((sum, value) => sum + value, 0) / points.length;

    // 2. 计算方差
    const variance = points.reduce((sum, value) => sum + Math.pow(value - mean, 2), 0) / points.length;
    // 3. 计算标准差
    const standardDeviation = Math.sqrt(variance);

    let val = points[0]
    for (let i = 1; i < points.length - 1; i++) {
      const currentValue = points[i];
      const diff = Math.abs(currentValue - mean) / standardDeviation
      if (diff > spikeThreshold) {
        points[i] = val
      }else {
        val = points[i]
      }
    }

    return points;
  }

  function performSpikeRemoval(deviceHist) {
    const compensationEntries = [
      { sensor: "phycocyanin" },
      { sensor: "chlorA" },
      { sensor: "turbidity" }
    ];
    let largeDeviceHist = computeLargeCompensatedHistory()
    compensationEntries.forEach((compensationEntry) => {
      const sensorIndex = deviceHist.sensors.findIndex((x) => x.sensorName === compensationEntry.sensor);
      const sensor = deviceHist.sensors[sensorIndex];

      const largeSensorIndex = largeDeviceHist.sensors.findIndex((x) => x.sensorName === compensationEntry.sensor);
      const largeSensor = largeDeviceHist.sensors[largeSensorIndex]

      const startIndex = largeSensor.history.length - sensor.history.length

      const sensorValues = largeSensor.history.map((item) => item.val);

      const threshold = usePreferenceStore().preference.options.spikeRemovalThreshold ?? 3;
      const sensorValuesWithoutSpike = spikeRemovalImpl(sensorValues, threshold);
      for (let index = 0; index < sensor.history.length; index++) {
        sensor.history[index].val = sensorValuesWithoutSpike[index+startIndex];
      }
    });
  }

  async function fetchDeviceSnapshot() {
    const authStore = useAuthStore();
    const authString = "Bearer " + authStore.authUser.authToken;
    const slotId = currentSlot.value._id;
    const fetchString = `${deviceRoot}/slots/${slotId}/report`;
    const response = await fetch(fetchString, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: authString,
      },
    });

    const data = await response.json();
    deviceSnapshot.value = data;
  }

  async function fetchTimeZone(payload) {
    const lat = payload.lat;
    const lng = payload.lng;
    const timeNow = Math.round(Date.now() / 1000);
    const geocodeUrl = `https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${timeNow}&key=AIzaSyA3T_UYc_CJJo_cb9n7y_yHpJ9Pl74mgiQ`;
    const response = await fetch(geocodeUrl);
    const data = await response.json();
    deviceTimeZone.value = data;
  }

  async function fetchWeatherLabel(payload) {
    const lat = payload.lat;
    const lng = payload.lng;
    const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyA3T_UYc_CJJo_cb9n7y_yHpJ9Pl74mgiQ`;
    const response = await fetch(geocodeUrl);
    const data = await response.json();
    deviceWeatherLabel.value = data;
  }

  async function fetchAccountUsers() {
    const authStore = useAuthStore();
    const authString = "Bearer " + authStore.authUser.authToken;
    const fetchString = `${authRoot}/users/`;

    const response = await fetch(fetchString, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: authString,
      },
    });
    const data = await response.json();
    accountUsers.value = data;
  }

  return {
    slots,
    currentSlot,
    deviceHistory,
    timeSeriesHistory,
    deviceSnapshot,
    deviceTimeZone,
    accountUsers,
    deviceWeatherLabel,
    createSlot,
    fetchSlot,
    fetchAccountSlots,
    fetchSlotSummary,
    updateSlot,
    fetchDeviceHistory,
    fetchDeviceSnapshot,
    fetchTimeZone,
    fetchWeatherLabel,
    fetchAccountUsers,
    computeCompensatedHistory,
    fetchTimeSeries,
    performSpikeRemoval,
    fetchNdvi
  };
});